
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { RequiredValidator } from "@dev/hc-components";
import CredentialsWorkflowCodeEnum from "@/app/dto/CredentialsWorkflowCodeEnum";
import Factor from "@/app/dto/FactorEnum";
import IAllowedFactorsResponse from "@/app/dto/IAllowedFactorsResponse";
import IAllowedFactor from "@/app/dto/IAllowedFactor";
import ICheckCredentialsResponse from "@/app/dto/ICheckCredentialsResponse";
import ICredentialsWorkflowStep from "@/app/dto/ICredentialsWorkflowStep";
import ProcessEnum from "@/app/dto/ProcessEnum";
import ProcessResultKey from "@/app/dto/ProcessResultKeyEnum";
import IApiError from "@/app/model/IApiError";
import RouteName from "@/app/router/NameEnum";
import AuthorizationService from "@/app/service/authorization";
import ConsentService from "@/app/service/consent";
import LoginService from "@/app/service/login";
import IConsentResponse from "@/app/dto/IConsentResponse";
import ConsentResultEnum from "@/app/dto/ConsentResultEnum";
import { getContactPhoneNumber } from "@/app/utils/EnvConfig";

@Component
export default class Login extends Vue {

    private username: string = "";
    private password: string = "";
    private usernameValidator: RequiredValidator = new RequiredValidator(this.$localize("login.username.required"));
    private passwordValidator: RequiredValidator = new RequiredValidator(this.$localize("login.password.required"));
    private invalidCredentials: boolean = false;
    private clientId: string = "";
    private responseType: string = "";
    private redirectUri: string = "";
    private phoneNumber: string = getContactPhoneNumber();
    private submitting: boolean = false;

    private get isValid(): boolean {
        const validUsername = this.usernameValidator.validate(this.username);
        const validPassword = this.passwordValidator.validate(this.password);
        return validUsername && validPassword;
    }

    private onSubmit(): void {
        this.clearErrors();
        if (this.isValid) {
            this.submitting = true;
            LoginService.checkCredentials(this.username, this.password)
                .then((response: ICheckCredentialsResponse) => {
                    if (this.hasLogin(response)) {
                        return this.getFactors(response.customerUuid);
                    } else {
                        let errorHeading: string = this.$localize("error.account.title");
                        let errorMessage: string = this.$localize("error.account.not_active.message");
                        this.routeToError({ heading: errorHeading, message: errorMessage });
                    }
                })
                .catch((error: IApiError) => {
                    this.processCredentialsError(error);
                })
                .finally(() => {
                    this.submitting = false;
                });
        }
    }

    private hasLogin(response: ICheckCredentialsResponse): boolean {
        return !!response.workflowSteps!!.find((w: ICredentialsWorkflowStep) => w.code == CredentialsWorkflowCodeEnum.AUTHORIZATION);
    }

    private processCredentialsError(error: IApiError): void {
        let errorHeading: string = "";
        let errorMessage: string = "";

        switch (error.status) {
            case ProcessResultKey.AUTHORIZATION_INVALID:
                this.invalidCredentials = true;
                break;
            case ProcessResultKey.ACCOUNT_BLOCKED:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.account.blocked.message");
                break;
            case ProcessResultKey.AUTHORIZATION_BLOCKED:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.authorization.blocked.message");
                break;
        }
        if (!this.invalidCredentials) {
            this.routeToError({ heading: errorHeading, message: errorMessage });
        }
    }

    private routeToError(params?: { heading: string; message: string }): void {
        this.$router.push({ name: RouteName.LOGIN_ERROR, params });
    }

    private clearErrors(): void {
        this.invalidCredentials = false;
    }

    private getFactors(customerUuid: string): Promise<void> {
        return AuthorizationService.getAllowedFactorsExternal(customerUuid, ProcessEnum.LOGIN)
            .then((response: IAllowedFactorsResponse) => {
                if (this.hasOtp(response)) {
                    this.$router.push({
                        name: RouteName.LOGIN_OTP,
                        params: {
                            customerUuid: customerUuid,
                            password: this.password,
                            clientId: this.clientId,
                            responseType: this.responseType,
                            redirectUri: this.redirectUri
                        }
                    });
                } else {
                    this.routeToError();
                }
            })
            .catch((error: IApiError) => {
                this.processAuthorizationKeyError(error);
            });
    }

    private hasOtp(response: IAllowedFactorsResponse): boolean {
        return !!response.data!!.find((f: IAllowedFactor) => f.factors!! && f.factors?.includes(Factor.OTP));
    }

    private processAuthorizationKeyError(error: IApiError) {
        if (error.status == ProcessResultKey.ACCOUNT_BLOCKED) {
            let errorHeading: string = this.$localize("error.account.title");
            let errorMessage: string = this.$localize("error.account.blocked.message");
            this.routeToError({ heading: errorHeading, message: errorMessage });
        }
        this.routeToError();
    }

    private checkConsent() {
        let clientId = this.$route.query.client_id as string || undefined;
        let responseType = this.$route.query.response_type as string || undefined;
        let redirectUri = this.$route.query.redirect_uri as string || undefined;
        ConsentService.check(clientId, responseType, redirectUri)
            .then((response: IConsentResponse) => {
                if (this.isAuthorized(response)) {
                    this.clientId = clientId || "";
                    this.responseType = responseType || "";
                    this.redirectUri = redirectUri || "";
                } else {
                    window.location.replace(response.redirectUrl);
                }
            })
            .catch((error: IApiError) => {
                this.processConsentError(error);
            });
    }

    private isAuthorized(response: IConsentResponse) {
        return response.result == ConsentResultEnum.AUTHORIZED;
    }

    private processConsentError(error: IApiError): void {
        let errorHeading: string = "";
        let errorMessage: string = "";

        if (error.status == ProcessResultKey.REQUIRED_REDIRECT_URI) {
            errorHeading = this.$localize("error.consent.title");
            errorMessage = this.$localize("error.consent.redirect_uri");
        }
        this.routeToError({ heading: errorHeading, message: errorMessage });
    }

    created() {
        this.checkConsent();
    }

}
